import is from '@sindresorhus/is';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';

import type {
    AgentChatMessage,
    AwaitingCallback,
    CallbackAction,
    ChatAttachmentRef,
    CreateJsaEntitiesMessageContent,
    FrontendContext,
    RegenerateMessageContent,
    Thread,
    UserMessage,
} from '../../../data-contract/chat';
import { useCancelChatTurn, useFetchChatThreadMessages, useMarkChatThreadRead } from '../../api';
import { cancelTurnWithRetry } from '../../api/cancel-turn-with-retry';
import type {
    AnswerQuestionOptions,
    ChatConversation,
    EnsureThreadIdOptions,
    PreparedSendOptions,
    SendOptions,
} from '../../chat-conversation';
import { useChatOpen, useChatStore } from '../../stores/chat-open-store';
import { getThreadStatus, isThreadOnHold, isThreadRunningOnBackend, type ThreadStatus } from '../../thread-status';
import { useChatThread as useChatThreadQuery, useSubmitChatInteractionCallback } from '../api';
import { setAssistantMessageStatus } from '../api/messages-cache';
import { useChatMessagesCache } from '../api/use-chat-messages-cache';
import { type ChatTurnState, generateLocalMessageId, useChatStreamRegistry } from './use-chat-stream-registry';
import { useChatThread } from './use-chat-thread';

export type { ChatTurnState, TurnStatus } from './use-chat-stream-registry';

export type PendingSend = {
    readonly hasFailed: boolean;
    readonly message: AgentChatMessage;
    readonly options: SendOptions;
};

export type SubmitCallbackAnswerOptions = {
    readonly actions: readonly CallbackAction[];
    readonly blockId: string;
    readonly eventId: string;
};

/** The legacy conversation: the thread's messages live in the query cache, the open turn in the stream registry. */
export type LegacyChatConversation = ChatConversation & {
    readonly awaitingCallback: AwaitingCallback | null;
    readonly clearTurn: () => void;
    readonly isFreshThread: boolean;
    /** Optimistic request rendered while its thread is still being created. */
    readonly pendingSend: PendingSend | null;
    readonly resumeTurn: () => Promise<void>;
    /** Sends the request behind the failed reply `failedMessageId` again. */
    readonly retry: (failedMessageId: string) => void;
    readonly submitCallbackAnswer: (options: SubmitCallbackAnswerOptions) => void;
    readonly turn: ChatTurnState;
};

const buildTextSendBody = (
    message: string,
    frontendContext: FrontendContext | undefined,
    attachments: readonly ChatAttachmentRef[] | undefined,
): UserMessage => ({
    ...(is.undefined(frontendContext) ? {} : { context: frontendContext }),
    ...(is.undefined(attachments) || attachments.length === 0 ? {} : { attachments: [...attachments] }),
    message,
    messageType: 'text',
    type: 'new',
});

const buildRegenerateSendBody = (
    content: RegenerateMessageContent,
    frontendContext: FrontendContext | undefined,
): UserMessage =>
    is.undefined(frontendContext)
        ? { content, type: 'regenerate' }
        : { content, context: frontendContext, type: 'regenerate' };

const buildOptimisticTextMessage = (
    trimmed: string,
    attachments: readonly ChatAttachmentRef[] | undefined,
): AgentChatMessage => ({
    ...(is.undefined(attachments) || attachments.length === 0 ? {} : { attachments: [...attachments] }),
    createdAt: new Date().toISOString(),
    id: generateLocalMessageId(),
    messageType: 'text',
    role: 'user',
    text: trimmed,
});

const buildOptimisticRegenerateMessage = (content: RegenerateMessageContent): AgentChatMessage => ({
    createdAt: new Date().toISOString(),
    id: generateLocalMessageId(),
    messageType: 'regenerate',
    regenerate: content,
    role: 'user',
});

const buildCreateJsaEntitiesSendBody = (
    content: CreateJsaEntitiesMessageContent,
    frontendContext: FrontendContext | undefined,
): UserMessage =>
    is.undefined(frontendContext)
        ? { content, type: 'create-jsa-entities' }
        : { content, context: frontendContext, type: 'create-jsa-entities' };

const buildOptimisticCreateJsaEntitiesMessage = (content: CreateJsaEntitiesMessageContent): AgentChatMessage => ({
    createdAt: new Date().toISOString(),
    createJsaEntities: content,
    id: generateLocalMessageId(),
    messageType: 'create-jsa-entities',
    role: 'user',
});

const buildOptimisticMessage = (options: PreparedSendOptions): AgentChatMessage => {
    if (options.messageType === 'regenerate') return buildOptimisticRegenerateMessage(options.content);
    if (options.messageType === 'create-jsa-entities') {
        return buildOptimisticCreateJsaEntitiesMessage(options.content);
    }
    return buildOptimisticTextMessage(options.message, options.attachments);
};

const buildSendBody = (options: PreparedSendOptions): UserMessage => {
    if (options.messageType === 'regenerate') {
        return buildRegenerateSendBody(options.content, options.frontendContext);
    }
    if (options.messageType === 'create-jsa-entities') {
        return buildCreateJsaEntitiesSendBody(options.content, options.frontendContext);
    }
    return buildTextSendBody(options.message, options.frontendContext, options.attachments);
};

// Agent actions are one-shot requests, not conversation turns: each opens its own thread
// instead of landing in whichever conversation happens to be selected. A text pinned to the
// thread its attachments were presigned against always stays there.
const needsFreshThread = async (
    options: PreparedSendOptions,
    leavesAgentActionThread: (isComposerMessage: boolean | undefined) => Promise<boolean>,
): Promise<boolean> => {
    if (options.messageType !== 'text') return true;
    return is.undefined(options.threadId) && (await leavesAgentActionThread(options.isComposerMessage));
};

const prepareSendOptions = (options: SendOptions): PreparedSendOptions | null => {
    if (options.messageType === 'regenerate' || options.messageType === 'create-jsa-entities') {
        return options;
    }

    const message = options.message.trim();
    return message.length === 0 ? null : { ...options, message, messageType: 'text' };
};

const prepareSendThread = ({
    awaitingCallback,
    cancellingThreadId,
    hasBackendRun,
    streamRegistry,
    threadId,
    tid,
}: {
    readonly awaitingCallback: AwaitingCallback | null;
    readonly cancellingThreadId: string | null;
    readonly hasBackendRun: boolean;
    readonly streamRegistry: ReturnType<typeof useChatStreamRegistry>;
    readonly threadId: string | null;
    readonly tid: string;
}): boolean => {
    // The server only cancels a stopped turn once its run lock is free; a new turn taking the lock
    // first would leave the stopped one uncancelled.
    if (cancellingThreadId === tid) return false;
    // A thread awaiting input keeps its backend run (and possibly the stream that
    // delivered the question) alive while it waits — the answer is delivered as a
    // regular message and must not be rejected by the concurrency guards below.
    const isAnsweringPendingInput = tid === threadId && !is.null(awaitingCallback);
    if (streamRegistry.hasLiveStream(tid)) {
        if (!isAnsweringPendingInput) return false;
        streamRegistry.stopTurn(tid);
    }
    if (tid === threadId && hasBackendRun && !isAnsweringPendingInput) return false;
    return true;
};

export const useChatConversation = (): LegacyChatConversation => {
    const chatCache = useChatMessagesCache();
    const fetchThreadMessages = useFetchChatThreadMessages();
    const {
        createFreshThread,
        ensureThreadId: ensureSelectedThreadId,
        isCreatingThread,
        isFreshThread,
        resetThread,
        selectThread: setActiveThread,
        threadId,
    } = useChatThread();
    const streamRegistry = useChatStreamRegistry(threadId);
    const submitCallback = useSubmitChatInteractionCallback();
    const { logger } = useApplicationContext();
    const cancelChatTurn = useCancelChatTurn();
    const [pendingSend, setPendingSend] = useState<PendingSend | null>(null);
    // Only the send that owns the buffer may touch it: a superseded thread creation resolving
    // late must not disturb the request a newer send is already showing.
    const clearPendingSend = useCallback((message: AgentChatMessage): void => {
        setPendingSend((current) => (current?.message === message ? null : current));
    }, []);
    const failPendingSend = useCallback((message: AgentChatMessage): void => {
        setPendingSend((current) => (current?.message === message ? { ...current, hasFailed: true } : current));
    }, []);

    const previousThreadIdRef = useRef(threadId);
    useEffect(() => {
        if (previousThreadIdRef.current === threadId) return;
        previousThreadIdRef.current = threadId;
        streamRegistry.cancelAutoClose();
    }, [streamRegistry.cancelAutoClose, threadId]);

    const activeThreadIdRef = useRef<string | null>(threadId);
    activeThreadIdRef.current = threadId;
    const hasResumedRef = useRef(false);
    // A stopped turn stays resumable on the server until its cancel lands (or for good, should the
    // cancel fail), so its thread is exempt from the auto-resume until the user sends to it again.
    const stoppedThreadIdRef = useRef<string | null>(null);
    const cancellingThreadIdRef = useRef<string | null>(null);
    const isRetryingSendRef = useRef(false);

    const threadQuery = useChatThreadQuery(threadId ?? undefined, {
        hasLiveStream: streamRegistry.hasLiveStream(threadId),
    });
    const thread = threadQuery.data;
    const pendingTurn = threadQuery.data?.pendingTurn;
    const awaitingCallback = pendingTurn?.awaitingCallback ?? null;
    const threadName = threadQuery.data?.name ?? null;
    const turn = streamRegistry.getTurn(threadId);
    const resolveThreadStatus = useCallback(
        (candidate: Thread): ThreadStatus =>
            getThreadStatus(candidate, { hasLiveStream: streamRegistry.hasLiveStream(candidate.id) }),
        [streamRegistry],
    );
    const threadStatus = is.nullOrUndefined(thread)
        ? streamRegistry.hasLiveStream(threadId)
            ? 'running'
            : 'idle'
        : resolveThreadStatus(thread);
    const hasBackendRun = !is.nullOrUndefined(thread) && isThreadRunningOnBackend(thread);

    // A thread is an agent action thread when its first request was a canvas regenerate or an add.
    // The first request never changes, so the answer is kept once known. Until the oldest page is
    // loaded it is unknown, so the older pages are fetched rather than guessing an ordinary thread.
    const agentActionThreadsRef = useRef(new Map<string, boolean>());
    const isAgentActionThread = useCallback(
        async (tid: string): Promise<boolean> => {
            const known = agentActionThreadsRef.current.get(tid);
            if (!is.undefined(known)) return known;
            let oldestPage = chatCache.getMessages(tid)?.pages.at(-1);
            while (is.undefined(oldestPage) || !is.nullOrUndefined(oldestPage.nextCursor)) {
                const result = await fetchThreadMessages(tid, { before: oldestPage?.nextCursor ?? undefined });
                if (result.isErr === true) throw result.error;
                oldestPage = result.value;
            }
            const firstRequest = oldestPage.messages.findLast((message) => message.role === 'user');
            if (is.undefined(firstRequest)) return false;
            const isAgentAction =
                firstRequest.messageType === 'regenerate' || firstRequest.messageType === 'create-jsa-entities';
            agentActionThreadsRef.current.set(tid, isAgentAction);
            return isAgentAction;
        },
        [chatCache, fetchThreadMessages],
    );

    // An agent action is a one-shot request in a thread of its own, so a new question typed into the
    // composer afterwards opens a new thread. An answer to a question the thread is waiting on stays.
    const leavesAgentActionThread = useCallback(
        async (isComposerMessage: boolean | undefined): Promise<boolean> =>
            isComposerMessage === true &&
            !is.null(threadId) &&
            is.null(awaitingCallback) &&
            (await isAgentActionThread(threadId)),
        [awaitingCallback, isAgentActionThread, threadId],
    );

    const ensureThreadId = useCallback(
        async (options?: EnsureThreadIdOptions): Promise<string | null> =>
            (await leavesAgentActionThread(options?.isComposerMessage))
                ? createFreshThread()
                : ensureSelectedThreadId(),
        [createFreshThread, ensureSelectedThreadId, leavesAgentActionThread],
    );

    // Null when the thread history needed for the decision failed to load, so the send is dropped.
    const resolveFreshThreadSend = useCallback(
        async (options: PreparedSendOptions): Promise<boolean | null> => {
            try {
                return await needsFreshThread(options, leavesAgentActionThread);
            } catch (error) {
                logger.error('Chat thread history failed to load; the request was not sent', error);
                return null;
            }
        },
        [leavesAgentActionThread, logger],
    );

    const resolveSendThreadId = useCallback(
        async (isFreshThreadSend: boolean, pinnedThreadId: string | undefined): Promise<string | null> =>
            isFreshThreadSend ? createFreshThread() : (pinnedThreadId ?? ensureSelectedThreadId()),
        [createFreshThread, ensureSelectedThreadId],
    );

    const startTurn = useCallback(
        (streamThreadId: string, body: UserMessage, messageId: string | null = null): void => {
            if (body.type !== 'resume' && stoppedThreadIdRef.current === streamThreadId) {
                stoppedThreadIdRef.current = null;
            }
            streamRegistry.startTurn(streamThreadId, body, messageId);
        },
        [streamRegistry],
    );

    const send = useCallback(
        async (options: SendOptions): Promise<boolean> => {
            const preparedOptions = prepareSendOptions(options);
            if (is.null(preparedOptions)) return false;

            // A send carrying attachments pins the thread they were presigned against;
            // resolving the thread again here could race a mid-upload thread switch.
            const pinnedThreadId = preparedOptions.messageType === 'text' ? preparedOptions.threadId : undefined;
            const isFreshThreadSend = await resolveFreshThreadSend(preparedOptions);
            if (is.null(isFreshThreadSend)) return false;
            const optimisticMessage = buildOptimisticMessage(preparedOptions);
            // Creating a thread is a round-trip; buffering the message renders the request and its
            // activity loader immediately instead of leaving the dock empty until the id lands.
            // Sends that reuse the open thread reach the cache without waiting, so they skip it.
            if (isFreshThreadSend) setPendingSend({ hasFailed: false, message: optimisticMessage, options });

            let tid: string | null;
            try {
                tid = await resolveSendThreadId(isFreshThreadSend, pinnedThreadId);
            } catch (error) {
                // The dock is already open at this point, so the buffered request stays and turns
                // into an error instead of leaving an unexplained empty conversation behind.
                logger.error('Chat thread creation failed; the request was not sent', error);
                failPendingSend(optimisticMessage);
                return false;
            }
            // A thread created for this send cannot be busy, so the concurrency guards are only
            // meaningful when the send targets the already-selected thread.
            if (
                is.null(tid) ||
                (!isFreshThreadSend &&
                    !prepareSendThread({
                        awaitingCallback,
                        cancellingThreadId: cancellingThreadIdRef.current,
                        hasBackendRun,
                        streamRegistry,
                        threadId,
                        tid,
                    }))
            ) {
                clearPendingSend(optimisticMessage);
                return false;
            }

            streamRegistry.appendUserMessage(tid, optimisticMessage);
            startTurn(tid, buildSendBody(preparedOptions));
            clearPendingSend(optimisticMessage);
            return true;
        },
        [
            awaitingCallback,
            clearPendingSend,
            failPendingSend,
            hasBackendRun,
            logger,
            resolveFreshThreadSend,
            resolveSendThreadId,
            startTurn,
            streamRegistry,
            threadId,
        ],
    );

    const resumeTurn = useCallback(async (): Promise<void> => {
        if (is.null(threadId)) return;
        startTurn(threadId, { resume: true, type: 'resume' }, pendingTurn?.messageId ?? null);
    }, [threadId, pendingTurn?.messageId, startTurn]);

    const submitCallbackAnswer = useCallback(
        ({ actions, blockId, eventId }: SubmitCallbackAnswerOptions): void => {
            const previousTurn = turn;
            const isTurnStreaming = previousTurn.status === 'streaming';
            const submittedThreadId = threadId;
            const answeredMessageId = pendingTurn?.messageId ?? null;
            // A streaming turn already owns its assistant message. Prefacing another
            // empty optimistic placeholder would become "latest", steal Accept/Reject UI,
            // and leave a spinner until refresh.
            const optimisticMessageId =
                is.null(submittedThreadId) || isTurnStreaming
                    ? null
                    : streamRegistry.ensurePendingMessage(submittedThreadId, answeredMessageId);
            submitCallback.mutate(
                { blockId, eventId, payload: { actions: [...actions] } },
                {
                    onSuccess: () => {
                        if (is.null(submittedThreadId)) return;
                        if (isTurnStreaming) return;
                        chatCache.invalidateAllChats();
                        if (activeThreadIdRef.current !== submittedThreadId) return;
                        if (hasBackendRun || streamRegistry.hasLiveStream(submittedThreadId)) return;
                        void resumeTurn();
                    },
                    onError: () => {
                        // The answer never reached the backend, so the question is open again.
                        useChatStore.getState().clearBlockAnswers(blockId);
                        if (is.null(submittedThreadId) || is.null(optimisticMessageId)) return;
                        streamRegistry.rollbackPendingMessage(
                            submittedThreadId,
                            optimisticMessageId,
                            previousTurn,
                            answeredMessageId,
                        );
                    },
                },
            );
        },
        [submitCallback, turn, threadId, pendingTurn?.messageId, chatCache, hasBackendRun, streamRegistry, resumeTurn],
    );

    const answerQuestion = useCallback(
        ({ actions, blockId, eventId }: AnswerQuestionOptions): void => {
            if (is.undefined(eventId)) return;
            submitCallbackAnswer({ actions, blockId, eventId });
        },
        [submitCallbackAnswer],
    );

    // A turn whose cancel failed only stays stoppable while its run is still active on the server;
    // once nothing runs, sending a new message is how the thread continues.
    const canStop = turn.status === 'streaming' || hasBackendRun;

    const stop = useCallback((): void => {
        const stoppedMessageId = turn.messageId;
        const stoppedLiveTurn = streamRegistry.stopTurn(threadId);
        // A run streaming elsewhere has no stream here but is still cancelled.
        if (is.null(threadId) || !(stoppedLiveTurn || canStop) || cancellingThreadIdRef.current === threadId) return;
        stoppedThreadIdRef.current = threadId;
        cancellingThreadIdRef.current = threadId;
        if (stoppedLiveTurn && !is.null(stoppedMessageId)) {
            chatCache.updateMessages(threadId, (data) => setAssistantMessageStatus(data, stoppedMessageId, 'complete'));
        }
        void cancelTurnWithRetry(cancelChatTurn, threadId).then((result) => {
            if (cancellingThreadIdRef.current === threadId) cancellingThreadIdRef.current = null;
            if (result.isErr) logger.error('Chat turn cancel failed; the turn stays resumable', result.error);
            chatCache.reconcileThreadAndList(threadId);
            // Only a settled turn is safe to refetch: before that, the refetch would discard what streamed in.
            if (result.isOk) chatCache.refetchMessages(threadId);
        });
    }, [cancelChatTurn, canStop, chatCache, logger, streamRegistry, threadId, turn.messageId]);

    const retry = useCallback(
        (failedMessageId: string): void => {
            if (pendingSend?.hasFailed === true) {
                if (isRetryingSendRef.current) return;
                isRetryingSendRef.current = true;
                void send(pendingSend.options).finally(() => {
                    isRetryingSendRef.current = false;
                });
                return;
            }
            if (is.null(threadId) || hasBackendRun || streamRegistry.hasLiveStream(threadId)) return;
            if (stoppedThreadIdRef.current === threadId) stoppedThreadIdRef.current = null;
            streamRegistry.retryTurn(threadId, failedMessageId);
        },
        [hasBackendRun, pendingSend, send, streamRegistry, threadId],
    );

    const selectThread = useCallback(
        (id: string): void => {
            streamRegistry.cancelAutoClose();
            setPendingSend(null);
            setActiveThread(id);
        },
        [setActiveThread, streamRegistry],
    );

    const startNewThread = useCallback((): void => {
        streamRegistry.cancelAutoClose();
        setPendingSend(null);
        resetThread();
    }, [resetThread, streamRegistry]);

    const clearTurn = useCallback((): void => streamRegistry.clearTurn(threadId), [streamRegistry, threadId]);

    const { mutate: markThreadRead } = useMarkChatThreadRead();
    const isChatOpen = useChatOpen();
    const isThreadUnread = thread?.unreadMessagesAvailable === true;
    // The provider stays mounted while the dock is closed, so gate on isChatOpen: content the
    // user cannot see must not be marked as read.
    useEffect(() => {
        if (!isChatOpen || is.null(threadId) || !isThreadUnread) return;
        markThreadRead({ threadId });
    }, [isChatOpen, threadId, isThreadUnread, markThreadRead]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: resets the resume-once latch whenever the active thread changes.
    useEffect(() => {
        hasResumedRef.current = false;
    }, [threadId]);

    useEffect(() => {
        if (is.null(threadId) || is.nullOrUndefined(thread)) return;
        // A settled thread re-arms the latch, because a thread can legitimately fall back on hold
        // within one selection when a later turn is interrupted. A resume that failed leaves the
        // thread on hold instead of idle, so it is still only ever retried once.
        if (threadStatus === 'idle') {
            hasResumedRef.current = false;
            return;
        }
        if (!isThreadOnHold(thread, { hasLiveStream: streamRegistry.hasLiveStream(thread.id) })) return;
        if (stoppedThreadIdRef.current === thread.id) return;
        if (turn.status === 'streaming') return;
        if (hasResumedRef.current) return;
        hasResumedRef.current = true;
        void resumeTurn();
    }, [threadId, thread, streamRegistry, threadStatus, turn.status, resumeTurn]);

    return useMemo(
        () => ({
            acceptAutoClose: streamRegistry.acceptAutoClose,
            answerQuestion,
            autoCloseScheduleId: streamRegistry.autoCloseScheduleId,
            awaitingCallback,
            canStop,
            clearTurn,
            ensureThreadId,
            isCreatingThread,
            isFreshThread,
            isAutoClosePending: streamRegistry.isAutoClosePending,
            isStreaming: turn.status === 'streaming',
            isThreadPending: !is.null(pendingSend),
            keepChatOpen: streamRegistry.keepChatOpen,
            pendingSend,
            resumeTurn,
            resolveThreadStatus,
            retry,
            selectThread,
            send,
            startNewThread,
            stop,
            submitCallbackAnswer,
            threadId,
            threadName,
            threadStatus,
            turn,
        }),
        [
            answerQuestion,
            awaitingCallback,
            canStop,
            clearTurn,
            ensureThreadId,
            isCreatingThread,
            isFreshThread,
            pendingSend,
            resumeTurn,
            resolveThreadStatus,
            retry,
            selectThread,
            send,
            startNewThread,
            stop,
            streamRegistry,
            submitCallbackAnswer,
            threadId,
            threadName,
            threadStatus,
            turn,
        ],
    );
};
